import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";

import { PostComposer } from "@/components/composer/post-composer";
import { PageHeader } from "@/components/page-header";
import { FailureDetailPanel } from "@/components/posts/failure-detail-panel";
import { PostDetailActions } from "@/components/posts/post-detail-actions";
import { PublishStatusPanel } from "@/components/posts/publish-status-panel";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { postDisplayTitle } from "@/lib/post-title";
import { useTRPC } from "@/utils/trpc";

export const Route = createFileRoute("/(app)/posts/$postId")({
  // The post detail page is hidden alongside the posts table — the calendar is
  // the primary surface for now. The component is preserved for a future
  // improved version; remove this `beforeLoad` to re-enable it. Until then a
  // /posts/:id link (AI apps linked here before 2026-09-29) opens the post's
  // details over the Publish list, the same place notification emails go.
  beforeLoad: ({ params }) => {
    throw redirect({
      href: `/calendar/week?layout=list&status=all&post=${encodeURIComponent(params.postId)}`,
      replace: true,
    });
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { postId } = Route.useParams();
  const { isLoading, workspaces } = useCurrentWorkspace();
  const trpc = useTRPC();
  const postQuery = useQuery(trpc.posts.get.queryOptions({ postId }));

  if (isLoading || postQuery.isPending) {
    return <Skeleton className="h-96 w-full" />;
  }

  if (postQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Post not found</AlertTitle>
        <AlertDescription>
          {postQuery.error.message} <Link to="/posts">Back to posts.</Link>
        </AlertDescription>
      </Alert>
    );
  }

  const postWorkspace = workspaces.find(
    (workspace) => workspace.id === postQuery.data.workspaceId,
  );
  const showFailurePanel =
    postQuery.data.status === "failed" ||
    postQuery.data.status === "partially_failed" ||
    postQuery.data.status === "publishing";

  if (!postWorkspace) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Workspace unavailable</AlertTitle>
        <AlertDescription>
          This post belongs to a workspace that is not available in the current
          session. <Link to="/posts">Back to posts.</Link>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={postDisplayTitle(postQuery.data)}
        description={`Version ${postQuery.data.version}`}
      />
      <PostDetailActions post={postQuery.data} workspace={postWorkspace} />
      <PublishStatusPanel postId={postQuery.data.id} />
      {showFailurePanel ? (
        <FailureDetailPanel postId={postQuery.data.id} />
      ) : null}
      <PostComposer post={postQuery.data} workspace={postWorkspace} />
    </div>
  );
}
